import React, { createContext, useState, useCallback, useEffect, ReactNode, useContext } from 'react';
import { Page, NavigationState } from '../types';

interface RouterContextType {
  currentNav: NavigationState;
  navigateTo: (state: NavigationState) => void;
}

export const RouterContext = createContext<RouterContextType | undefined>(undefined);

// Use HashRouter is now disabled as per user request for clean URLs.
const useHashRouter = false;

const PAGE_TO_PATH: Record<string, string> = {
    'home': '/',
    'enroll-now': '/enroll',
    'cma-bundle-offer': '/cma-bundle-success-package',
    'articles': '/blog',
    'article-detail': '/blog',
    'official-resources': '/resources',
    'part-detail': '/cma', // Base path, handled specifically
    'cma-faqs': '/faqs',
};

export const getSemanticPath = (state: NavigationState): string => {
    if (state.page === 'home') return '/';
    
    if (state.page === 'part-detail' && state.params?.code) {
        if (state.params.code === 'part1') return '/cma-part-1';
        if (state.params.code === 'part2') return '/cma-part-2';
    }

    if (state.page === 'article-detail' && state.params?.slug) {
        return `/blog/${state.params.slug}`;
    }

    const path = PAGE_TO_PATH[state.page] || `/${state.page}`;
    return path;
};

const getStateFromPath = (path: string): NavigationState => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    if (cleanPath === '/' || cleanPath === '') return { page: 'home' };
    if (cleanPath === '/enroll') return { page: 'enroll-now' };
    if (cleanPath === '/cma-part-1') return { page: 'part-detail', params: { code: 'part1' } };
    if (cleanPath === '/cma-part-2') return { page: 'part-detail', params: { code: 'part2' } };
    if (cleanPath === '/cma-bundle-success-package') return { page: 'cma-bundle-offer' };
    if (cleanPath === '/blog') return { page: 'articles' };
    if (cleanPath === '/resources') return { page: 'official-resources' };
    if (cleanPath === '/faqs') return { page: 'cma-faqs' };
    
    if (cleanPath.startsWith('/blog/')) {
        const slug = cleanPath.substring(6);
        return { page: 'article-detail', params: { slug } };
    }

    // Default matching for other pages
    const page = cleanPath.substring(1) as Page;
    if (validPages.includes(page)) return { page };

    return { page: 'not-found' };
};

const parseState = (pagePath: string, searchStr: string | undefined, hashStr: string | undefined): NavigationState => {
    const state = getStateFromPath(pagePath);

    if (searchStr) {
        const searchParams = new URLSearchParams(searchStr);
        const params: { [key: string]: string } = state.params || {};
        for (const [key, value] of searchParams.entries()) {
            params[key] = value;
        }
        if (Object.keys(params).length > 0) {
            state.params = params;
        }
    }

    if (hashStr) {
        state.hash = hashStr;
    }

    return state;
}

// Derives navigation state from the URL
const getStateFromUrl = (): NavigationState => {
    const { pathname, search, hash } = window.location;
    return parseState(pathname, search.substring(1), hash.substring(1));
};


const validPages: Page[] = ['home', 'cma-course-details', 'cma-experience-requirement', 'cma-course-fee', 'about', 'contact', 'enroll-now', 'part-detail', 'testimonials', 'privacy-policy', 'refund-policy', 'terms-and-conditions', 'dipifrs', 'cma-bundle-offer', 'demo-videos', 'articles', 'official-resources', 'article-detail', 'cma-faqs', 'not-found'];

export const RouterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentNav, setCurrentNav] = useState<NavigationState>(getStateFromUrl());

  // This effect synchronizes the component's state with the URL.
  // It's responsible for reacting to browser back/forward buttons.
  useEffect(() => {
    const handleStateChange = () => {
      setCurrentNav(getStateFromUrl());
    };
    
    window.addEventListener('popstate', handleStateChange);
    return () => {
      window.removeEventListener('popstate', handleStateChange);
    };
  }, []);

  // This effect handles scrolling AFTER a navigation has occurred and the new page has rendered.
  // It runs whenever the 'currentNav' state changes.
  useEffect(() => {
    const timerId = setTimeout(() => {
        if (currentNav.hash) {
            const element = document.getElementById(currentNav.hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, 0);

    return () => clearTimeout(timerId);
  }, [currentNav]);


  const navigateTo = useCallback((state: NavigationState) => {
    const semanticPath = getSemanticPath(state);
    let newUrl = semanticPath;

    // Filter out logic-level params that are now part of the semantic path
    const searchParams = new URLSearchParams();
    if (state.params) {
        for (const [key, value] of Object.entries(state.params)) {
             // If the param is already in the semantic path (like code or slug), don't add to query
             const isCodeInPath = state.page === 'part-detail' && (value === 'part1' || value === 'part2');
             const isSlugInPath = state.page === 'article-detail';
             
             if (!isCodeInPath && !isSlugInPath) {
                 searchParams.append(key, value);
             }
        }
    }

    if (searchParams.toString()) {
        newUrl += `?${searchParams.toString()}`;
    }
    
    if (state.hash) {
        newUrl += `#${state.hash}`;
    }
    
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    if (currentUrl !== newUrl) {
        window.history.pushState(state, '', newUrl);
        // Manually update state because pushState doesn't trigger popstate
        setCurrentNav(state);
    } else {
        // If the URL is identical but there's a hash, it means we are trying to scroll
        // to a section on the same page.
        if (state.hash) {
            const element = document.getElementById(state.hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }
  }, []);

  const value = { currentNav, navigateTo };

  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
};

export const useRouter = (): RouterContextType => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useRouter must be used within a RouterProvider');
  }
  return context;
};